import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { authService } from '../services/auth';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 2rem;
`;

const Card = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h1`
  color: #333;
  margin: 0 0 1.5rem 0;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 0.875rem;
  text-align: center;
`;

const SetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setError('Invalid or missing token. Please check your email link.');
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Invalid token. Please check your email link.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.setPassword(token, password);
      
      if (result.token) {
        // User is now automatically signed in
        setSuccess('Password set successfully! Signing you in...');

        // Track Lead event for new user signup via email
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Email Sign Up',
            content_category: 'signup'
          });
        }

        // Track Google Analytics event
        if (window.gtag) {
          window.gtag('event', 'sign_up', {
            method: 'Email'
          });
        }

        setTimeout(() => {
          navigate('/subscription');
        }, 1500);
      } else {
        // Fallback to manual sign in
        setSuccess(result.message + ' Please sign in with your new password.');
        setTimeout(() => {
          navigate('/?signin=true');
        }, 2000);
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Failed to set password';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token && !error) {
    return (
      <Container>
        <Card>
          <Title>Loading...</Title>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Set Your Password</Title>
        {success ? (
          <SuccessMessage>
            {success}
            <br />
            <br />
            Redirecting to sign in...
          </SuccessMessage>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Input
              type="password"
              placeholder="New Password (min 8 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              minLength={8}
            />
            <Input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Button type="submit" disabled={isLoading || !token}>
              {isLoading ? 'Setting Password...' : 'Set Password'}
            </Button>
          </Form>
        )}
      </Card>
    </Container>
  );
};

export default SetPassword; 